import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface AccountingImportFiltersTabProps {
  categories: string[];
  showExcluded: boolean;
  selectedCategories: string[];
  onShowExcludedChange: (show: boolean) => void;
  onCategoriesChange: (categories: string[]) => void;
}

export function AccountingImportFiltersTab({
  categories,
  showExcluded,
  selectedCategories,
  onShowExcludedChange,
  onCategoriesChange,
}: AccountingImportFiltersTabProps) {
  const [displayExpanded, setDisplayExpanded] = useState(true);
  const [categoryExpanded, setCategoryExpanded] = useState(true);

  // Calculate active filter count
  const activeFilterCount = (showExcluded ? 1 : 0) + (selectedCategories.length > 0 ? 1 : 0);

  // Handle clear all
  const handleClearAll = () => {
    onShowExcludedChange(false);
    onCategoriesChange([]);
  };

  // Handle category toggle
  const handleCategoryToggle = (category: string) => {
    if (selectedCategories.includes(category)) {
      onCategoriesChange(selectedCategories.filter(c => c !== category));
    } else {
      onCategoriesChange([...selectedCategories, category]);
    }
  };

  // Handle select all categories
  const handleSelectAllCategories = () => {
    if (selectedCategories.length === categories.length) {
      onCategoriesChange([]);
    } else {
      onCategoriesChange([...categories]);
    }
  };

  const allCategoriesSelected = selectedCategories.length === categories.length && categories.length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header - Fixed */}
      <div className="flex-shrink-0 px-4 pt-4 pb-3 border-b bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <Badge variant="default" className="h-5 px-2 text-xs">
                {activeFilterCount}
              </Badge>
            )}
          </div>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="h-7 px-2 text-xs"
            >
              <RotateCcw className="mr-1.5 h-3 w-3" />
              Clear All
            </Button>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {/* Display Options Section */}
          <Collapsible open={displayExpanded} onOpenChange={setDisplayExpanded}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <CollapsibleTrigger className="flex items-center gap-2 hover:text-foreground transition-colors group">
                  {displayExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  )}
                  <Label className="text-sm font-semibold cursor-pointer">Display Options</Label>
                </CollapsibleTrigger>
                {showExcluded && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    1
                  </Badge>
                )}
              </div>

              <CollapsibleContent className="space-y-2">
                <div className="flex items-center space-x-2.5 py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors">
                  <Checkbox
                    id="show-excluded"
                    checked={showExcluded}
                    onCheckedChange={(checked) => onShowExcludedChange(checked as boolean)}
                    className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  />
                  <label
                    htmlFor="show-excluded"
                    className="text-sm font-normal leading-none cursor-pointer flex-1 select-none"
                  >
                    Show already imported assets
                  </label>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          <Separator />

          {/* Category Filter Section */}
          <Collapsible open={categoryExpanded} onOpenChange={setCategoryExpanded}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <CollapsibleTrigger className="flex items-center gap-2 hover:text-foreground transition-colors group">
                  {categoryExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  )}
                  <Label className="text-sm font-semibold cursor-pointer">Category</Label>
                </CollapsibleTrigger>
                {selectedCategories.length > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    {selectedCategories.length}/{categories.length}
                  </Badge>
                )}
              </div>

              <CollapsibleContent className="space-y-2">
                {/* Select All Option */}
                <div className="flex items-center justify-end mb-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSelectAllCategories}
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                  >
                    {allCategoriesSelected ? 'Clear' : 'Select All'}
                  </Button>
                </div>

                {/* Category Options */}
                {categories.map((category) => (
                  <div
                    key={category}
                    className="flex items-center space-x-2.5 py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors"
                  >
                    <Checkbox
                      id={`category-${category}`}
                      checked={selectedCategories.includes(category)}
                      onCheckedChange={() => handleCategoryToggle(category)}
                      className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    />
                    <label
                      htmlFor={`category-${category}`}
                      className="text-sm font-normal leading-none cursor-pointer flex-1 select-none"
                    >
                      {category}
                    </label>
                  </div>
                ))}
              </CollapsibleContent>
            </div>
          </Collapsible>
        </div>
      </div>

      {/* Empty State */}
      {activeFilterCount === 0 && (
        <div className="flex-shrink-0 border-t bg-muted/20">
          <div className="text-center py-6 px-4">
            <p className="text-sm text-muted-foreground">No filters applied</p>
            <p className="text-xs text-muted-foreground mt-1">Select filters above to refine results</p>
          </div>
        </div>
      )}
    </div>
  );
}
